// canvasProjectRedirect.js — the workspace shell's catch-all classifier.
//
// tickets/workspace.md "should the workspace shell serve project CANVAS
// routes at all? (`/<owner>/<project>`)" — RESOLVED 2026-07-28, per Nika:
// option (b), serve them IN-SHELL. pages/projectRedirect.js now lazily folds
// the full canvas graph and mounts the editor page for project-shaped paths;
// this file only classifies which unmatched paths are project-shaped.
//
// History, because it explains two shipped bugs: option (a) — "redirect to
// the host that runs the editor" — was implemented here as
// `canvasProjectRedirectUrl = workspaceUrl(...)`, i.e. it redirected to the
// WORKSPACE SHELL ITSELF: the very host whose inability to serve the route
// triggered the redirect. On prod that was a same-URL reload of a page that
// still couldn't serve it; in dev it resolved to the dead `my.localhost`
// host (until workspace 00c8728a) — which is where the "New project" flow
// was actually dumping users. The builder is deleted; do not reintroduce it.

// Every first-path-segment this shell (or canvas, via its OWN explicit,
// non-catch-all page keys — dashboard/account/signup/studios/team-and-agents/
// agents) already owns (pages/index.js + manifest.js's route table). A path
// under one of these never redirects, even a deep sub-path with no exact
// page match (it 404s in-shell instead) — a close-miss under a REAL top
// segment must never bounce to a different host.
//
// Bare shared-library import, not a deep projectUrls.js subpath — see
// app.js's identical fix note (tickets/catalog-fixall.md P0). Deferred-call
// wrapper, not a module-scope destructure — see app.js's circular-init note.
import { shellParseWorkspaceProjectPath } from './shellSharedDelegates.js'

// `w` is the reserved multi-workspace-tabs org-slug prefix (`/w/<org-slug>/
// ...` — normally stripped to the real route before the router ever sees it,
// mwtRouter.js/workspacePrefix.js — but guarded here too since a hard-load
// or a stale/shared link could reach this catch-all with the prefix still
// attached).
//
// EXCEPTION (2026-08-06 per Nika): the workspace-scoped canvas-project form
// `/w/<org>/p/<key>` / `/w/<org>/<ws>/p/<key>` IS project-shaped — its
// stripped route (`/p/<key>`) matches no page key, so it lands on this
// catch-all by design and must mount the in-shell editor. The reserved `p`
// marker makes the parse positional (projectUrls.js), so it classifies
// correctly even on a cold hard-load before workspace data resolves.
export const CANVAS_REDIRECT_RESERVED_SEGMENTS = new Set([
  'w',
  // Auth + session
  'login',
  'signin',
  'signup',
  'signup-confirmation',
  'reset-password',
  'forgot-password',
  'verify-email',
  'auth',
  'github-auth-callback',
  '_session-bridge',
  'demo',
  // Domain libraries mounted at the shell root
  'tickets',
  'chat',
  'chats',
  'calendar',
  'inbox',
  'epics',
  'teams',
  'meet',
  'meet-guest',
  'notes',
  'documents',
  'docs',
  // Shell-owned intranet pages
  'admin',
  'people',
  'my',
  'announcements',
  'company',
  'data',
  'data-external',
  // 'system' is deliberately NOT reserved: the bare `/system` page was
  // unregistered so `/system/<key>` opens the "system" org's projects in
  // the canvas editor (see the `/system-status` note in pages/index.js —
  // the admin page lives at `/admin/system`). Listing it here re-created
  // the very shadowing that removal fixed: the catch-all classified
  // /system/platform as not-project-shaped and 404'd it (2026-07-29 per
  // Nika).
  'system-status',
  'integrations',
  'features',
  'profile',
  'settings',
  'user',
  'u',
  'add-app',
  // Lazy shared-library sub-apps mounted at the shell root
  'marketplace',
  'preview',
  'infra',
  'analytics',
  'workspace-analytics',
  'logs',
  // Canvas surfaces with their OWN explicit (non-catch-all) page keys —
  // unaffected by dropping canvas's '/*', reserved here too for deep
  // unmapped sub-paths under them.
  'dashboard',
  'account',
  'studios',
  'studio',
  'team-and-agents',
  'agents',
  // The POB getting-started pages (/getting-started/team, /imports): an
  // unmapped step under them is the shell's not-found, never the editor.
  'getting-started'
])

// Classify an UNMATCHED pathname (nothing in context.pages matched it, so
// the router fell through to the catch-all) as a canvas project path
// (`/<owner>/<key>` or the workspace-scoped `/w/<org>[/<ws>]/p/<key>` — mount
// the in-shell editor) or not (render the shell's plain not-found instead).
// Pure — no window/env access — directly testable.
export const isCanvasProjectPath = (pathname) => {
  const path = String(pathname || '')
  const segments = path.split('/').filter(Boolean)
  if (segments.length < 2) return false // a bare `/<owner>` alone names nothing to redirect to
  const first = segments[0].toLowerCase()
  // `/w/…` is reserved intranet space EXCEPT its canvas-project form — the
  // positional `p` marker decides (see the header note above).
  if (first === 'w') return !!shellParseWorkspaceProjectPath(path)
  return !CANVAS_REDIRECT_RESERVED_SEGMENTS.has(first)
}
